<template>
  <m-field
    :data-testid="testId"
    :status="hasError ? 'danger' : 'default'"
    :class="{ 'flex-1': !width }">
    <m-field-label v-if="label" slot="label" :data-testid="`${testId}-label`">
      {{ label }}

      <help-tooltip v-if="hint" :text="hint" />
    </m-field-label>
    <vue-tel-input
      ref="telInput"
      :style="computedWidth"
      @focus="handleFocus"
      @focusout="handleBlur"
      mode="international"
      :value="lazyValue"
      @input="handleInput"
      :dropdownOptions="{
        showDialCodeInSelection: true,
        showFlags: false,
        showDialCodeInList: true
      }"
      :inputOptions="{
        placeholder:
          $attrs.placeholder ??
          $t('forms.elements.inputs.phoneNumberField.placeholders.enterAPhoneNumber')
      }"
      valid-characters-only
      :all-countries="countryOptions"
      @country-changed="onCountryChanged">
      <template #arrow-icon>
        <basic-icon>chevron-down</basic-icon>
      </template>
    </vue-tel-input>
    <m-field-hint :data-testid="`${testId}-errors`">
      {{ errorBucket.join(', ') }}
    </m-field-hint>
  </m-field>
</template>

<script>
import fieldMixin from '@/components/mixins/fieldMixin';
import validatable from '@/components/mixins/validatable';
import testable from '@/components/mixins/testable';
import { getCountryCallingCodes, ISO3166FilteredCountries } from '@nova/core';
import { BasicIcon, HelpTooltip } from '@/components';

/**
 * Phone number field with country code options.  Wraps https://www.npmjs.com/package/vue-tel-input
 * @displayName Phone Number Field
 */
export default {
  name: 'PhoneNumberField',
  mixins: [fieldMixin, validatable, testable],
  components: { BasicIcon, HelpTooltip },
  props: {
    /**
     * @model
     */
    modelValue: {
      type: String,
      required: false,
      default: null
    },
    /**
     * Field wrapper width
     */
    width: {
      type: String,
      required: false,
      default: '400px'
    },
    testId: {
      type: String,
      required: false,
      default: 'luna-phone-number-field'
    }
  },
  data() {
    return {
      selectedCountry: null,
      countryOptions: [],
      ISO3166FilteredCountries
    };
  },
  methods: {
    onCountryChanged(val) {
      this.selectedCountry = val;
      this.validateInput();
    },
    validateInput() {
      this.$nextTick(() => {
        const input = this.$refs.telInput.modelValue;
        this.validate(this.focusCount !== 0, input, [this.$validator.rules.phone[0](input)]);
      });
    },
    handleInput(value) {
      /**
       * vue3-tel-input can emit non-string payloads on `input`;
       * only strings represent the text value.
       * Before first focus, the widget may emit `''` while we already have a model;
       * ignore so we do not clear the field.
       */
      if (typeof value === 'string' && value.length > 0) {
        this.internalValue = value;
        this.validateInput();
      }
    }
  },
  mounted() {
    this.countryOptions = getCountryCallingCodes().map(country => {
      return {
        name: country.name,
        iso2: country.iso2,
        dialCode: country.callingCode
      };
    });
  }
};
</script>

<style lang="scss" scoped>
.phone-field {
  max-width: 275px;
}

.vue-tel-input {
  border: none;
  display: flex;
  align-items: center;
  gap: 0;
  &:focus-within {
    outline-style: none;
    box-shadow: none;
    border-color: transparent;
  }
}

:deep(input) {
  min-height: 37px !important;
  max-height: 37px !important;
  font-style: normal;
  font-stretch: normal;
  letter-spacing: normal;
  font-synthesis: none;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-text-size-adjust: 100%;
  font-family: var(--m-font-family-default, 'Manrope', sans-serif);
  line-height: var(--m-line-height-5, 150%);
  font-size: var(--m-font-size-3, 0.875rem);
  font-weight: var(--m-font-weight-regular, 500);
  align-self: stretch;
  border: none;
  outline: none;
  flex: 1;
  min-width: 0;
  padding-inline-end: 0;
  padding-inline-start: 0;
  padding-block: var(--m-text-field-padding-y);
  background-color: var(--m-text-field-background-color);
  color: var(--m-text-field-color);
  --m-text-field-background-color: rgba(var(--m-color-background-primary, 252, 252, 255), 1);
  --m-text-field-border-color: rgba(var(--m-color-border, 193, 206, 217), 1);
  --m-text-field-height: var(--m-global-height-default, 2.5rem);
  --m-text-field-padding-y: var(--m-spacing-2, 8px);
  --m-text-field-placeholder-color: rgba(var(--m-color-text-placeholder, 94, 118, 138), 1);
  --m-text-field-color: rgba(var(--m-color-text-primary, 32, 41, 49), 1);
  font-style: normal;
  font-stretch: normal;
  letter-spacing: normal;
  font-synthesis: none;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-text-size-adjust: 100%;
  font-family: var(--m-font-family-default, 'Manrope', sans-serif);
  line-height: var(--m-line-height-5, 150%);
  font-size: var(--m-font-size-3, 0.875rem);
  font-weight: var(--m-font-weight-regular, 500);
  transition:
    background-color 0.35s ease-in-out 0s,
    border-color 0.35s ease-in-out 0s;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--m-spacing-2, 8px);
  position: relative;
  box-sizing: border-box;
  width: 100%;
  block-size: var(--m-text-field-height);
  padding-inline-end: var(--m-spacing-2, 8px);
  padding-inline-start: var(--m-spacing-2, 8px);
  background-color: var(--m-text-field-background-color);
  border-radius: var(--m-border-radius-s, 4px);
  border: 1px solid var(--m-text-field-border-color);
  &:focus {
    --m-text-field-border-color: rgba(var(--m-color-primary-100, 0, 51, 51), 1);
  }
}

:deep(.vti__dropdown) {
  max-width: 125px;
  min-height: 37px !important;
  max-height: 37px !important;
  width: 70px;
  margin-right: 8px;
  --m-text-field-background-color: rgba(var(--m-color-background-primary, 252, 252, 255), 1);
  --m-text-field-border-color: rgba(var(--m-color-border, 193, 206, 217), 1);
  --m-text-field-height: var(--m-global-height-default, 2.5rem);
  --m-text-field-padding-y: var(--m-spacing-2, 8px);
  --m-text-field-placeholder-color: rgba(var(--m-color-text-placeholder, 94, 118, 138), 1);
  --m-text-field-color: rgba(var(--m-color-text-primary, 32, 41, 49), 1);
  font-style: normal;
  font-stretch: normal;
  letter-spacing: normal;
  font-synthesis: none;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-text-size-adjust: 100%;
  font-family: var(--m-font-family-default, 'Manrope', sans-serif);
  line-height: var(--m-line-height-5, 150%);
  font-size: var(--m-font-size-3, 0.875rem);
  font-weight: var(--m-font-weight-regular, 500);
  transition:
    background-color 0.35s ease-in-out 0s,
    border-color 0.35s ease-in-out 0s;
  display: inline-flex;
  justify-content: flex-start;
  gap: var(--m-spacing-2, 8px);
  position: relative;
  box-sizing: border-box;
  block-size: var(--m-text-field-height);
  padding-inline-end: var(--m-spacing-2, 8px);
  padding-inline-start: var(--m-spacing-2, 8px);
  background-color: var(--m-text-field-background-color);
  border-radius: var(--m-border-radius-s, 4px);
  border: 1px solid var(--m-text-field-border-color);

  &:focus {
    --m-text-field-border-color: rgba(var(--m-color-primary-100, 0, 51, 51), 1);
  }
}

:deep(.vti__selection) {
  justify-content: space-between;
}

:deep(.vti__dropdown-list) {
  padding: 0;
  z-index: 1000;
}

:deep(.vti__dropdown-item) {
  width: 100%;
  padding: $m-spacing-2 $m-spacing-2 !important;
}

/**
  Removing via css instead of component options to preserve the dropdown styling
 */
:deep(.vti__flag-wrapper) {
  display: none !important;
}

:deep(.tel-input) {
  @media (max-width: $tabletBreakpoint) {
    width: 100% !important;
  }
}
</style>
